import React, { useEffect } from 'react';

import { UiSelect } from '../../ui/ui-select';
import { UiTextField } from '../../ui/ui-text-field';
import { useWebhookEventSubscriptionContext } from '../context/webhook-event-subscription-context';

import {
  ACCEPTED_STATUS,
  EMPLOYED_STATUS,
  INTERVIEWING_STATUS,
  PHONE_INTERVIEWING_STATUS,
  statusDisplay,
  VALIDATING_STATUS,
} from '../../config/application-status-config';
import { capitalize } from '../../util/formatters';
import {
  INOVA_CREATE_RECORD_TYPE,
  WEBHOOK_EVENT_SUBSCRIPTION_EVENT_TRIGGER_TYPE,
  WEBHOOK_EVENT_SUBSCRIPTION_EVENT_TYPE,
  WebhookEventSubscriptionEventTriggerType,
} from '../utils/webhook-event-subscription-utils';

type InputProps = {
  org: Org;
  isDeleting: boolean;
  isEditing: boolean;
};

export const InovaWebhookEventSubscriptionFormBody = (props: InputProps) => {
  const { org, isDeleting, isEditing } = props;

  const {
    setEventType,
    eventTriggerType,
    setEventTriggerType,
    subscriptionDetail,
    setSubscriptionDetail,
  } = useWebhookEventSubscriptionContext();

  // On initial render of this component, set default event values
  useEffect(() => {
    setEventType(WEBHOOK_EVENT_SUBSCRIPTION_EVENT_TYPE.APPLICANT_STATUS_CHANGE);
    // if we are creating a new subscription, set default eventTriggerType
    // and default subscriptionDetail.createRecordType
    if (!isEditing) {
      setEventTriggerType(WEBHOOK_EVENT_SUBSCRIPTION_EVENT_TRIGGER_TYPE.VALIDATING);
      setSubscriptionDetail({
        createRecordType: INOVA_CREATE_RECORD_TYPE.EMPLOYEE,
        companyShortName: '',
      });
    } else {
      // If there is no subscriptionDetail.createRecordType when editing, default to employee
      setSubscriptionDetail({
        createRecordType: subscriptionDetail?.createRecordType || INOVA_CREATE_RECORD_TYPE.EMPLOYEE,
        companyShortName: subscriptionDetail?.companyShortName,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- we want this to only load on initial render
  }, []);

  const handleChangeTriggerType = (triggerType: string) => {
    setEventTriggerType(triggerType as WebhookEventSubscriptionEventTriggerType);
  };

  const handleChangeCreateRecordType = (createRecordType: string) => {
    setSubscriptionDetail((prevState: Record<string, any>) => ({ ...prevState, createRecordType }));
  };

  const handleSetCompanyShortName = (companyShortName: string) => {
    setSubscriptionDetail((prevState: Record<string, any>) => ({ ...prevState, companyShortName }));
  };

  const usesPhoneInterview = org?.usesPhoneInterview || false;
  const triggerTypeStatuses = usesPhoneInterview
    ? [
        ACCEPTED_STATUS,
        PHONE_INTERVIEWING_STATUS,
        INTERVIEWING_STATUS,
        VALIDATING_STATUS,
        EMPLOYED_STATUS,
      ]
    : [ACCEPTED_STATUS, INTERVIEWING_STATUS, VALIDATING_STATUS, EMPLOYED_STATUS];
  const triggerTypeOptions = triggerTypeStatuses.map((opt) => ({
    label: statusDisplay(opt),
    value: opt,
  }));

  const createRecordTypeOptions = [
    INOVA_CREATE_RECORD_TYPE.APPLICANT,
    INOVA_CREATE_RECORD_TYPE.EMPLOYEE,
  ].map((opt) => ({
    label: capitalize(opt),
    value: opt,
  }));

  return (
    <>
      {!isEditing && (
        <UiTextField
          label="Customer Short Name"
          value={subscriptionDetail?.companyShortName || ''}
          onChange={handleSetCompanyShortName}
        />
      )}
      <UiSelect
        disabled={isDeleting}
        className="u-margin-top-lg"
        label="Status Change to Create Employee in Inova"
        value={eventTriggerType || ''}
        options={triggerTypeOptions}
        onChange={handleChangeTriggerType}
      />
      <UiSelect
        disabled={isDeleting}
        className="u-margin-top-lg"
        label="Create applicant in Inova as"
        value={subscriptionDetail?.createRecordType || ''}
        options={createRecordTypeOptions}
        onChange={handleChangeCreateRecordType}
      />
    </>
  );
};
